import React, { useState } from 'react';
import { Check, AlertTriangle, FileText, FileImage, Tag, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';

export const qualityOf = (plan, model) => {
  const checks = [];
  const files = (plan.uploaded_files || []).length;
  checks.push({ ok: files > 0, text: files > 0 ? `Ritningar analyserade (${files} st)` : 'Inga ritningar — kalkylen bygger på beskrivning' });
  checks.push({ ok: (plan.phases || []).length >= 3, text: `${(plan.phases || []).length} byggdelar med ${(plan.materials || []).length} materialrader` });
  const bm = plan.benchmark_check?.status;
  checks.push({ ok: bm === 'ok', text: bm === 'ok' ? 'Totalsumma inom branschsnitt (kr/m²)' : bm ? `Avvikelse mot branschsnitt: ${plan.benchmark_check?.deviation_pct ?? ''}%` : 'Ingen branschjämförelse för projekttypen' });
  checks.push({ ok: !!model && model.source !== 'procedural', text: model ? (model.source === 'procedural' ? '3D-modell schematisk (ingen ritning)' : '3D-modell byggd från ritning') : '3D-modell skapas…' });
  const unresolved = plan.reasoning_breakdown?.unresolved_measurements || [];
  checks.push({ ok: unresolved.length === 0, text: unresolved.length ? `Osäkerhet: ${unresolved.length} mått behöver verifieras` : 'Alla mått kunde läsas av' });
  const low = (plan.materials || []).filter((m) => m.confidence === 'low').length;
  const score = Math.round(Math.max(40, Math.min(97, 55 + checks.filter((c) => c.ok).length * 8 - Math.min(10, low) + (files ? 2 : 0))));
  return { score, checks, unresolved };
};

const Ring = ({ score }) => {
  const r = 30; const c = 2 * Math.PI * r;
  return (
    <svg width="76" height="76" viewBox="0 0 76 76" data-testid="ai-quality-score">
      <circle cx="38" cy="38" r={r} fill="none" stroke="#EEE9E2" strokeWidth="7" />
      <circle cx="38" cy="38" r={r} fill="none" stroke="#16A34A" strokeWidth="7" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - score / 100)} transform="rotate(-90 38 38)" />
      <text x="38" y="43" textAnchor="middle" fontSize="16" fontWeight="800" fill="#141312">{score}%</text>
    </svg>
  );
};

export const AIQualityCard = ({ plan, model }) => {
  const [open, setOpen] = useState(false);
  const q = qualityOf(plan, model);
  const rb = plan.reasoning_breakdown || {};
  return (
    <div className="op-card p-5" data-testid="ai-quality-card">
      <div className="flex items-center justify-between">
        <h3 className="text-[15px] font-bold flex items-center gap-2"><ShieldCheck size={17} className="text-[#F26B1F]" />AI-analys & kvalitetssäkring</h3>
        <span className="text-[10.5px] font-semibold rounded-md bg-emerald-50 text-emerald-700 px-2 py-0.5">Verifierad</span>
      </div>
      <div className="mt-3 flex items-center gap-4">
        <Ring score={q.score} />
        <div><p className="text-sm font-semibold">Beräkningsnoggrannhet</p><p className="text-xs text-stone-500">Baserat på tillgängligt underlag och kontroller.</p></div>
      </div>
      <ul className="mt-3 space-y-1.5">
        {q.checks.map((c, i) => (
          <li key={i} className="flex items-start gap-2 text-[12.5px]">
            {c.ok ? <Check size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" /> : <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />}{c.text}
          </li>
        ))}
      </ul>
      <button onClick={() => setOpen(true)} className="op-btn-ghost w-full mt-4 h-10 text-[13px]" data-testid="ai-report-btn">Visa detaljerad rapport</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-report-dialog">
          <DialogHeader><DialogTitle>AI-rapport</DialogTitle></DialogHeader>
          {plan.ai_summary && <p className="text-sm leading-relaxed">{plan.ai_summary}</p>}
          {!!(rb.key_assumptions || []).length && (<><h4 className="font-semibold text-sm mt-2">Antaganden</h4><ul className="list-disc pl-5 text-sm space-y-1">{rb.key_assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul></>)}
          {!!q.unresolved.length && (<><h4 className="font-semibold text-sm mt-2">Mått att verifiera</h4><ul className="list-disc pl-5 text-sm space-y-1">{q.unresolved.map((a, i) => <li key={i}>{a}</li>)}</ul></>)}
          {plan.benchmark_check?.message && <p className="text-sm rounded-xl bg-stone-100 p-3">{plan.benchmark_check.message}</p>}
          {plan.file_analysis && (<><h4 className="font-semibold text-sm mt-2">Ritningsanalys</h4><pre className="whitespace-pre-wrap text-xs bg-stone-50 rounded-xl p-3 max-h-72 overflow-y-auto">{plan.file_analysis}</pre></>)}
          <p className="text-[11px] text-stone-400">Modell: {plan.model_used || '—'} · Prompt: {plan.prompt_version || '—'}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const SourcesCard = ({ plan }) => {
  const suppliers = [...new Set((plan.materials || []).map((m) => (m.where_to_buy || '').split(/[,/]/)[0].trim()).filter(Boolean))];
  const items = [
    { icon: FileImage, title: 'Ritningar', sub: `${(plan.uploaded_files || []).length} filer`, testid: 'source-blueprints' },
    { icon: FileText, title: 'Projektbeskrivning', sub: (plan.notes || plan.job_type || '—').slice(0, 80), testid: 'source-description' },
    { icon: Tag, title: 'Priser', sub: suppliers.length ? `${suppliers.slice(0, 4).join(', ')}${suppliers.length > 4 ? ` +${suppliers.length - 4}` : ''}` : 'Svenska byggvaruhus 2026', testid: 'source-prices' },
  ];
  return (
    <div className="op-card p-5" data-testid="sources-card">
      <h3 className="text-[15px] font-bold mb-2">Underlag & källor</h3>
      {items.map((it) => (
        <div key={it.title} className="flex items-start gap-3 py-2" data-testid={it.testid}>
          <it.icon size={18} className="text-stone-500 mt-0.5" />
          <div className="min-w-0"><p className="text-[13px] font-semibold">{it.title}</p><p className="text-[11.5px] text-stone-500 line-clamp-2">{it.sub}</p></div>
        </div>
      ))}
    </div>
  );
};
